'use client';

import { useFormatter, useTranslations } from 'next-intl';
import { type MouseEvent, useState } from 'react';
import { UserAvatar } from '@/components/images/user-avatar';
import { AnimatedButton } from '@/components/ui/animated-button';
import { AppTooltip } from '@/components/ui/app-tooltip';
import { Icon } from '@/components/ui/icon';
import { Comment03IconSolid, LinkForwardIconSolid } from '@/components/ui/icon/glyphs';
import { D6_SCALE } from '@/config/solana';
import { useIntentRoutePrefetch } from '@/hooks/use-intent-route-prefetch';
import { Link } from '@/i18n/navigation';
import { useFormattedCurrency } from '@/lib/currency';
import { withDemoMode } from '@/lib/network';
import { useUserNetwork } from '@/lib/network/client';
import { resolveCanonicalOrigin } from '@/lib/seo';
import { ShareModal } from '@/lib/share';
import { cn } from '@/lib/ui/utils';
import type { GiftRowData } from './gift-row';

/** Fixed width + left margin for status column: Claim, Pending (sent), Claimed — avoids layout shift
 *  between states. The `ml-2` widens the gap to the amount column on its left without affecting the
 *  trailing share button's gap (the outer row uses a tighter `gap-2`). */
const COMPACT_GIFT_CLAIM_STATUS_FIXED_WIDTH_CLASS = 'ml-2 w-[5.5rem]';

/**
 * Placeholder until the outcomes SDK exposes the on-chain Token-2022 symbol
 * (e.g. `$XYZYES`). Replace the body with `outcome.symbol` from `useGetOutcome`
 * once the backend route ships it.
 */
function getOutcomeTickerPlaceholder(outcomeLabel: string | null | undefined): string | null {
  if (outcomeLabel === null || outcomeLabel === undefined || outcomeLabel.length === 0) {
    return null;
  }
  return `$${outcomeLabel.toUpperCase()}`;
}
export type CompactGiftRowVariant = 'received' | 'sent';
export interface CompactGiftRowProps {
  /** Tab the row belongs to. Drives counterparty resolution + trailing status control. */
  variant: CompactGiftRowVariant;
  /** Underlying gift — same shape consumed by the legacy `<GiftRow>` (FR-033 reuse). */
  data: GiftRowData;
  /** Optional className passthrough (used by parents that need to tweak spacing). */
  className?: string;
}

/**
 * Compact gift row used in `<GiftsSummaryCard>` and `<AllGiftsDialog>`.
 *
 * Layout (DOM-sibling click separation per spec FR-019 / research §R2):
 *
 *   [Link  flex-1 min-w-0  →  /gifts/<id> or /gifts/<id>/claim]
 *      avatar | name+date | amount+asset
 *   [Status — Claim link (received pending), or disabled status label]
 *   [Share — opens ShareModal scoped to this gift]
 */
export function CompactGiftRow({
  variant,
  data,
  className
}: CompactGiftRowProps): React.ReactNode {
  const tRow = useTranslations('gift.compactRow');
  const tGiftRowStatus = useTranslations('gift.inbox.row.status');
  const tProfile = useTranslations('profile');
  const formatter = useFormatter();
  const {
    formatD6
  } = useFormattedCurrency();
  const {
    network
  } = useUserNetwork();
  const [shareOpen, setShareOpen] = useState<boolean>(false);
  const isPendingReceived: boolean = variant === 'received' && data.status === 'pending';
  const isPendingSent: boolean = variant === 'sent' && data.status === 'pending';
  const isClaimedReceived: boolean = variant === 'received' && data.status === 'claimed';
  const isClaimedSent: boolean = variant === 'sent' && data.status === 'claimed';
  // Withdrawn status removed in the new contract — always false.
  const isWithdrawnSent = false;
  const isAnonymous: boolean = variant === 'received' && (data.anonymous || data.senderHandle === null);
  const counterpartyHandle: string = variant === 'received' ? data.senderHandle ?? '' : data.recipientHandle;
  const counterpartyLabel: string = isAnonymous ? tProfile('anonymousMorfian') : `@${counterpartyHandle}`;
  const formattedDate: string = formatter.dateTime(new Date(data.createdAt), {
    dateStyle: 'medium'
  });
  const isUsdc: boolean = data.assetKind === 'usdc';
  const usdcAmount: string = formatD6(data.amount, 'compact');
  const outcomeShares: string = ((): string => {
    try {
      return (BigInt(data.amount) / BigInt(D6_SCALE)).toString();
    } catch {
      // SILENT-CATCH: non-numeric gift amount strings cannot be scaled — fall back to raw display
      return data.amount;
    }
  })();
  const amountText: string = isUsdc ? usdcAmount : `${outcomeShares}`;
  const assetText: string = ((): string => {
    if (isUsdc) return tRow('assetCash');
    const ticker = getOutcomeTickerPlaceholder(data.outcomeLabel);
    if (ticker !== null) return ticker;
    if (data.marketTitle !== null && data.marketTitle !== undefined && data.marketTitle.length > 0) {
      return data.marketTitle;
    }
    return tRow('assetOutcomeFallback');
  })();
  const claimHref: string = `/gifts/${data.opaqueId}/claim`;
  const navigationHref: string = isPendingReceived ? claimHref : `/gifts/${data.opaqueId}`;
  const navigationIntent = useIntentRoutePrefetch(navigationHref);
  const shareUrl: string = ((): string => {
    try {
      const u = new URL(`/gifts/${data.opaqueId}`, resolveCanonicalOrigin());
      withDemoMode(u, network === 'mainnet' ? 'mainnet' : 'devnet');
      return u.toString();
    } catch {
      // SILENT-CATCH: canonical origin or query mutation failed — relative path is enough for modal copy UX
      return `/gifts/${data.opaqueId}`;
    }
  })();
  const statusSuffixForAria: string = ((): string => {
    if (isPendingReceived || isPendingSent) return tRow('statusPendingSuffix');
    if (isClaimedSent || isClaimedReceived) return tRow('statusClaimedSuffix');
    if (isWithdrawnSent) return tRow('statusReturnedSuffix');
    return '';
  })();
  const ariaLabel: string = ((): string => {
    const params: Record<string, string> = {
      amount: amountText,
      asset: assetText,
      statusSuffix: statusSuffixForAria
    };
    if (variant === 'received') {
      return tRow('ariaLabelReceived', {
        ...params,
        sender: counterpartyLabel
      });
    }
    return tRow('ariaLabelSent', {
      ...params,
      recipient: counterpartyLabel
    });
  })();
  const avatarSeed: string = isAnonymous ? counterpartyLabel : counterpartyHandle;
  function handleShareClick(e: MouseEvent<HTMLButtonElement>): void {
    e.stopPropagation();
    setShareOpen(true);
  }
  const claimedTestId = `compact-gift-row-claimed-${data.opaqueId}`;
  let statusControl: React.ReactNode | null = null;
  if (isPendingReceived) {
    statusControl = <AnimatedButton asChild variant="secondary" size="xs" className={COMPACT_GIFT_CLAIM_STATUS_FIXED_WIDTH_CLASS} aria-label={tRow('claimCtaAriaLabel', {
      counterparty: counterpartyLabel
    })}>
        <Link href={claimHref} prefetch={false} data-testid={`compact-gift-row-claim-${data.opaqueId}`}>
          {tRow('claimCta')}
        </Link>
      </AnimatedButton>;
  } else if (isClaimedReceived) {
    statusControl = <AnimatedButton disabled type="button" variant="secondary" size="xs" className={COMPACT_GIFT_CLAIM_STATUS_FIXED_WIDTH_CLASS} aria-label={tRow('claimedStatusAriaLabel', {
      counterparty: counterpartyLabel
    })} data-testid={claimedTestId}>
        {tRow('statusClaimed')}
      </AnimatedButton>;
  } else if (isPendingSent) {
    statusControl = <AnimatedButton disabled type="button" variant="secondary" size="xs" className={COMPACT_GIFT_CLAIM_STATUS_FIXED_WIDTH_CLASS} aria-label={tRow('pendingSentStatusAriaLabel', {
      counterparty: counterpartyLabel
    })} data-testid={`compact-gift-row-sent-pending-${data.opaqueId}`}>
        {tGiftRowStatus('pending')}
      </AnimatedButton>;
  } else if (isClaimedSent) {
    statusControl = <AnimatedButton disabled type="button" variant="secondary" size="xs" className={COMPACT_GIFT_CLAIM_STATUS_FIXED_WIDTH_CLASS} aria-label={tRow('recipientClaimedStatusAriaLabel', {
      counterparty: counterpartyLabel
    })} data-testid={claimedTestId}>
        {tGiftRowStatus('claimed')}
      </AnimatedButton>;
  } else if (isWithdrawnSent) {
    statusControl = <AnimatedButton disabled type="button" variant="secondary" size="xs" className={COMPACT_GIFT_CLAIM_STATUS_FIXED_WIDTH_CLASS} aria-label={tRow('returnedSentStatusAriaLabel', {
      counterparty: counterpartyLabel
    })} data-testid={`compact-gift-row-sent-returned-${data.opaqueId}`}>
        {tGiftRowStatus('withdrawn')}
      </AnimatedButton>;
  }
  return <div className={cn('flex items-center gap-2', className)}>
      <Link href={navigationHref} prefetch={false} {...navigationIntent} aria-label={ariaLabel} className="flex flex-1 min-w-0 items-center gap-3 rounded-control py-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-primary" data-testid={`compact-gift-row-${data.opaqueId}`}>
        {/* ALLOW-NSFW-FALSE: gift SDK doesn't expose counterparty avatarUrl or nsfw — fallback avatar is a deterministic palette tile, not user-uploaded content. */}
        <UserAvatar userId={null} displayName={avatarSeed} avatarUrl={null} size="sm" nsfw={false}
      // ALLOW-RASTER-LITERAL: gift SDK doesn't expose counterparty avatarPreferences; the fallback avatar has no photo to rasterise.
      rasterise={false} />
        <div className="flex flex-1 min-w-0 items-center justify-between gap-3">
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="flex min-w-0 items-center gap-1.5">
              <span className="truncate text-body font-medium text-foreground-primary">
                {counterpartyLabel}
              </span>
            </span>
            <span className="truncate text-caption text-foreground-tertiary">{formattedDate}</span>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-0.5 text-right">
            <span className="text-body font-medium text-foreground-primary">{amountText}</span>
            <span className="max-w-36 truncate text-caption text-foreground-tertiary">
              {assetText}
            </span>
          </div>
        </div>
      </Link>
      {data.message.length > 0 ? <AppTooltip content={`“${data.message}”`} side="top">
          <span className="inline-flex size-7 shrink-0 items-center justify-center text-foreground-secondary" data-testid={`compact-gift-row-message-${data.opaqueId}`} role="img" aria-label={tRow('messageAriaLabel')}>
            <Icon icon={Comment03IconSolid} className="size-4" />
          </span>
        </AppTooltip> : null}
      {statusControl}
      <AnimatedButton type="button" variant="tertiary" size="xs" prefix={LinkForwardIconSolid} onClick={handleShareClick} aria-label={tRow('shareAriaLabel', {
      counterparty: counterpartyLabel
    })} data-testid={`compact-gift-row-share-${data.opaqueId}`} />
      <ShareModal open={shareOpen} onOpenChange={setShareOpen} url={shareUrl} title={tRow('shareAriaLabel', {
      counterparty: counterpartyLabel
    })} surface={{
      kind: 'gift',
      opaqueId: data.opaqueId,
      ...(data.depositPubkey !== undefined ? {
        depositPubkey: data.depositPubkey
      } : {})
    }} />
    </div>;
}